<template>
  <div>
    <h1>Orders</h1>

    <v-overlay v-if="syncing" z-index="998">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>
    <v-btn color="primary" @click="syncOrders()" class="buttonStyle">
      Sync
    </v-btn>
    <v-btn
      color="cyan"
      class="buttonStyle"
      @click="$refs.fileInput.click()"
      >Upload Tracking</v-btn
    >
    <input
      ref="fileInput"
      style="display: none"
      type="file"
      @change="handleTrackingFileUpload"
      accept=".xlsx, .xls"
    />
    <v-data-table
      v-if="orders"
      :headers="headers"
      :items="orders"
      :items-per-page="pageItems"
      :search="searchTerm"
      class="elevation-1 table datatable"
      :custom-filter="customSearch"
      :loading="loading"
      loading-text="Loading... Please wait"
    >
      <template v-slot:[`header.status`]="{ header }">
        {{ header.text }}
        <v-menu offset-y :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon small color="surface"> mdi-filter </v-icon>
            </v-btn>
          </template>
          <div style="background-color: white; width: 280px">
            <v-select
              label="Filter Status"
              :items="status"
              item-text="status"
              outline
              dense
              color="secondary"
              class="select mr-3"
              v-model="selectedStatus"
              @input="onFilterStatus"
              ><template #item="{ item }">
                <span align-center> {{ item.status }}</span>
              </template>
            </v-select>
            <v-btn
              @click="cleanStatusFilter"
              small
              text
              color="primary"
              class="ml-2 mb-2"
              >Clean</v-btn
            >
          </div>
        </v-menu>
      </template>
      <template v-slot:[`item.id`]="{ item }">
        <v-row class="shopify_id">
          {{ item.shopify_order_number }}
          <router-link :to="{ name: 'OrderDetails', params: { id: item.id } }">
              <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" fill="currentColor" class="bi bi-box-arrow-in-right" viewBox="0 0 16 16">
                <path fill-rule="evenodd" d="M6.5 3a.5.5 0 0 0-.5.5v2A.5.5 0 0 0 6.5 6h7a.5.5 0 0 0 0-1h-6.5V3.5a.5.5 0 0 0-.5-.5zm4 4a.5.5 0 0 1 .5-.5h2.5a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-.5.5H11a.5.5 0 0 1-.5-.5V8a.5.5 0 0 1 .5-.5zM9.146 9.354a.5.5 0 0 1 0-.708l2.5-2.5a.5.5 0 0 1 .708 0l2.5 2.5a.5.5 0 0 1-.708.708L12 7.707V13a.5.5 0 0 1-1 0V7.707l-1.646 1.647a.5.5 0 0 1-.708 0z"/>
              </svg>
            </router-link>
        </v-row>
        <v-row class="date">
          {{ formatDate(item.createdAt) }}
        </v-row>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <v-row>
          <v-chip
            :color="getStatusBackgroundColor(item.status)"
            class="status"
            >{{ item.status }}</v-chip
          >
        </v-row>
        <v-row>
          <v-select
            label="Change Status"
            :items="status"
            item-text="status"
            solo
            dense
            class="select mr-3"
            v-model="item.status"
            @input="onStatusChange(item.id, item.status)"
            ><template #item="{ item }">
              <span align-center> {{ item.status }}</span>
            </template>
          </v-select>
        </v-row>
      </template>
      <template v-slot:[`item.name`]="{ item }">
        <v-row>
          {{ item.order_shipping.first_name }}
          {{ item.order_shipping.last_name }}
        </v-row>
        <v-row v-if="item.customer" style="text-align: center">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="orderInfo"
                style="color: #0099ff; background-color: #99ebff"
              >
                T:{{ item.customer.total_orders }}
              </span>
            </template>
            <span>Total Orders: {{ item.customer.total_orders }}</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="orderInfo"
                style="color: #00cc00; background-color: #66ff66"
              >
                C:{{ item.customer.completed_orders }}
              </span>
            </template>
            <span>Completed Orders: {{ item.customer.completed_orders }}</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="orderInfo"
                style="color: #cc3300; background-color: #ff4d4d"
              >
                R:{{ item.customer.returned_orders }}
              </span>
            </template>
            <span>Returned Orders: {{ item.customer.returned_orders }}</span>
          </v-tooltip>
          <!--<span v-if="item.review"
              ><v-icon color="yellow">mdi-star</v-icon
              >{{ item.review.rating }}</span
            >-->
        </v-row>
      </template>
      <template v-slot:[`item.products`]="{ item }">
        <v-row>
          <v-col class="ma-0 pb-0">
            {{ productString(item.order_products) }}
          </v-col>
        </v-row>
        <v-row v-if="item.status == 'Delivered'">
          Review Sent: {{ item.review != null ? "Yes" : "No" }}

          <span>
            <v-btn
              class="cnEditIcon"
              text
              icon
              color="green lighten-2"
              @click="sendReviewAgain(item.id)"
            >
              <v-icon>mdi-reload</v-icon>
            </v-btn>
          </span>
        </v-row>
      </template>
      <template v-slot:[`item.price`]="{ item }">
        <v-row> Rs. {{ item.total_price }} </v-row>
      </template>
      <template v-slot:[`item.address`]="{ item }">
        <v-row>
          <v-col class="ma-0 pb-0"> {{ item.order_shipping.address1 }} </v-col>
        </v-row>
        <v-row>
          <v-col v-if="item.phone" class="ma-0 pt-0">
            {{ item.phone }}
          </v-col>
          <v-col v-else-if="item.order_shipping.phone" class="ma-0 pt-0">
            {{ item.order_shipping.phone }}
          </v-col>
        </v-row>

        <v-row>
          <v-col class="ma-0 pt-0">
            {{ item.order_shipping.city }}
          </v-col>
        </v-row>
      </template>
      <template v-slot:[`item.delivery`]="{ item }">
        <v-row v-if="item.order_delivery">
          <v-chip
            :color="getCourierServiceColor(item.order_delivery.company)"
            class="status"
            >{{ item.order_delivery.company }}</v-chip
          >
        </v-row>
        <v-row v-if="item.order_delivery">
          <span>{{ item.order_delivery.cn }}</span>
          <span
            v-if="
              item.order_delivery.company == 'TCS' ||
              item.order_delivery.company == 'LEOPARD'
            "
          >
            <v-btn
              class="cnEditIcon"
              text
              icon
              color="blue lighten-2"
              @click="openCNDialog(item.id)"
            >
              <v-icon>mdi-pencil-circle</v-icon>
            </v-btn>
          </span>
          <span
            v-if="
              item.order_delivery.company == 'TCS' ||
              item.order_delivery.company == 'LEOPARD' ||
              item.order_delivery.cn.includes('WR')
            "
          >
            <v-btn
              class="cnEditIcon"
              text
              icon
              color="surface"
              @click="getTrackingInformation(item.id)"
            >
              <v-icon>mdi-information</v-icon>
            </v-btn>
          </span>
        </v-row>
        <v-row v-if="item.order_delivery == null">
          <v-select
            label="Courier Service"
            :items="courierCompanies"
            solo
            dense
            class="select mr-3"
            item-key="item.id"
            @input="bookCourierService(item.id, $event)"
            ><template #item="{ item }">
              <span align-center> {{ item }}</span>
            </template>
          </v-select>
        </v-row>
      </template>
      <template v-slot:no-data>
        <v-btn color="primary" @click="getAllOrders"> Reset </v-btn>
      </template>
    </v-data-table>

    <!--SNACKBAR FOR ERRORS-->
    <v-snackbar v-model="snackbar">
      {{ snackBarText }}

      <template>
        <v-btn color="secondary" text @click="snackbar = false"> Close </v-btn>
      </template>
    </v-snackbar>

    <!--DIALOG TO UPDATE CN-->
    <v-dialog v-model="cnDialog" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Update Tracking Number</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  label="Tracking Number"
                  hint="73636272"
                  v-model="cn"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="cnDialog = false">
            Close
          </v-btn>
          <v-btn color="blue darken-1" text @click="updateCN()"> Update </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!--DIALOG TO SHOW TRACKING-->
    <v-dialog v-model="trackingDialog" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Tracking Information</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row v-if="order_tracking">
              <v-col cols="12">
                <span>Status: {{ order_tracking.status }}</span>
              </v-col>
            </v-row>
            <v-row v-if="order_tracking">
              <v-data-table
                v-if="order_tracking.details"
                :headers="tracking_headers"
                :items="order_tracking.details"
                class="elevation-1 table datatable"
                :loading="loading"
                loading-text="Loading... Please wait"
              >
              </v-data-table>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="trackingDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
    
    <script>
import axios from "axios";
import * as XLSX from "xlsx";
import { BASE_URL } from "./baseUrl";
/* eslint-disable */
export default {
  name: "OrderView",
  data() {
    return {
      headers: [
        {
          text: "Id",
          align: "start",
          sortable: false,
          value: "id",
        },
        { text: "Status", value: "status", sortable: false, filterable: true },
        { text: "Name", value: "name", sortable: false, filterable: false },
        {
          text: "Products",
          value: "products",
          sortable: false,
          filterable: false,
        },
        {
          text: "Total Price",
          value: "price",
          sortable: false,
          filterable: false,
        },
        {
          text: "Address",
          value: "address",
          sortable: false,
          filterable: false,
        },
        {
          text: "Delivery",
          value: "delivery",
          sortable: false,
          filterable: false,
        },
      ],
      status: [
        {
          status: "Processing",
          color: "#f1c63b",
        },
        {
          status: "On hold",
          color: "#ED7322",
        },
        {
          status: "Completed",
          color: "#008000",
        },
        {
          status: "Cancelled",
          color: "#36454f",
        },
        {
          status: "Refunded",
          color: "#a7a6ba",
        },
        {
          status: "No answer",
          color: "#8f8b66",
        },
        {
          status: "Return",
          color: "#D70f1e",
        },
        {
          status: "Confirmed",
          color: "#0f95d7",
        },
        {
          status: "Delivered",
          color: "#Be0fd7",
        },
        {
          status: "Dispatched",
          color: "#cc66ff",
        },
        {
          status: "Paid",
          color: "#ff4d94",
        },
      ],
      options: {
        headers: {
          "Access-Control-Allow-Headers":
            "X-Custom-Header, Access-Control-Request-Headers",
          "x-access-token": localStorage.getItem("token"),
          //"Access-Control-Allow-Headers":"Access-Control-Allow-Origin"
        },
      },
      tracking_headers: [
        {
          text: "Time",
          align: "start",
          sortable: true,
          value: "time",
        },
        { text: "Status", value: "status", sortable: false, filterable: false },
        {
          text: "Recieved By",
          value: "recievedBy",
          sortable: false,
          filterable: false,
        },
      ],
      courierCompanies: ["TCS", "Leopard"],
      totalOrders: 0,
      orders: null,
      pageItems: 10,
      page: 1,
      loading: true,
      order: null,
      syncing: false,
      searchTerm: null,
      snackbar: false,
      snackBarText: "",
      selectedStatus: "",
      ordersCopy: null,
      company: "",
      cnDialog: false,
      cn: "",
      orderId: 0,
      order_tracking: null,
      trackingDialog: false,
      riders: null,
      totalRiders: 0,
    };
  },
  watch: {},
  mounted() {
    this.syncing = true;
    let options = {
      headers: {
        "Access-Control-Allow-Headers":
          "X-Custom-Header, Access-Control-Request-Headers",
        "x-access-token": localStorage.getItem("token"),
        //"Access-Control-Allow-Headers":"Access-Control-Allow-Origin"
      },
    };
    var url = BASE_URL + `orders/`;
    axios
      .get(url, options)
      .then((response) => {
        console.log(response);
        this.orders = response.data.orders;
        this.ordersCopy = this.orders;
        this.totalOrders = response.data.totalCount;
        this.loading = false;
        this.syncing = false;
      })
      .catch((error) => {
        console.log(error);
        this.errored = true;
      })
      .finally(() => (this.loading = false));
    var riderUrl = BASE_URL + `riders/getAll`;
    axios
      .get(riderUrl, options)
      .then((response) => {
        console.log(response);
        this.riders = response.data.riders;
        this.totalRiders = response.data.totalCount;
        this.loading = false;
        if (this.totalRiders > 0) {
          this.riders.forEach((rider) => {
            this.courierCompanies.push(rider.name);
          });
        }
      })
      .catch((error) => {
        console.log(error);
        this.errored = true;
      })
      .finally(() => (this.loading = false));
  },
  methods: {
    getAllOrders() {
      this.syncing = true;
      var url = BASE_URL + `orders/`;
      axios
        .get(url, this.options)
        .then((response) => {
          console.log(response);
          this.orders = response.data.orders;
          this.totalOrders = response.data.totalCount;
          this.ordersCopy = this.orders;
          this.loading = false;
          this.syncing = false;
        })
        .catch((error) => {
          console.log(error);
          this.errored = true;
        })
        .finally(() => (this.loading = false));
    },
    customSearch(value, search, item) {
      let searchArr = search.split(" ");
      return searchArr.every((item) =>
        value.toLowerCase().includes(item.toLowerCase())
      );
    },
    editItem(item) {
      this.order = item;
      this.dialog = true;
    },

    syncOrders() {
      this.syncing = true;
      var url = BASE_URL + "orders/sync";
      axios
        .get(url, this.options)
        .then((response) => {
          console.log(response.data);

          if (response.data.hasError == true) {
            this.snackbar = true;
            this.snackBarText = response.data.message;
          } else if (response.status == 200) {
            this.snackbar = true;
            this.snackBarText = response.data;
            this.syncing = false;
            this.getAllOrders();
          }
        })
        .catch((error) => {
          console.log(error);
          this.errored = true;
        })
        .finally(() => (this.loading = false));
    },

    productString(products) {
      var desc = "";
      products.forEach((prod) => {
        desc += prod.quantity + " x " + prod.title + " ";
      });
      return desc;
    },

    formatDate(date) {
      var newDate = new Date(date);
      return newDate.toLocaleDateString();
    },

    getStatusBackgroundColor(orderStatus) {
      for (var i = 0; i < this.status.length; i++) {
        if (this.status[i].status == orderStatus) {
          return this.status[i].color;
        }
      }
    },

    onStatusChange(id, selectedStatus) {
      console.log(selectedStatus);
      var url =
        BASE_URL + "orders/status?orderId=" + id + "&status=" + selectedStatus;
      axios
        .put(url, "", this.options)
        .then((response) => {
          console.log(response.data);

          if (response.data.hasError == true) {
            this.snackbar = true;
            this.snackBarText = response.data.message;
          } else if (response.data.hasError == false) {
            this.snackbar = true;
            this.snackBarText = response.data.message;
            this.changeCustomerProfile(id, selectedStatus);
          }
        })
        .catch((error) => {
          console.log(error);
          this.errored = true;
        });
    },

    changeCustomerProfile(id, status) {
      for (var i = 0; i < this.orders.length; i++) {
        if (this.orders[i].id == id) {
          if (status == "Return") {
            this.orders[i].customer.completed_orders =
              this.orders[i].customer.total_orders -
              this.orders[i].customer.returned_orders -
              1;
            this.orders[i].customer.returned_orders =
              this.orders[i].customer.returned_orders + 1;
          } else if (status == "Paid" || status == "Completed") {
            this.orders[i].customer.returned_orders =
              this.orders[i].customer.total_orders -
              this.orders[i].customer.completed_orders -
              1;
            this.orders[i].customer.completed_orders =
              this.orders[i].customer.completed_orders + 1;
          }
          break;
        }
      }
    },
    onFilterStatus() {
      this.orders = this.ordersCopy;
      this.orders = this.orders.filter((el) => {
        return el.status == this.selectedStatus;
      });
    },

    cleanStatusFilter() {
      (this.selectedStatus = ""), (this.orders = this.ordersCopy);
    },
    bookCourierService(id, $event) {
      var courierCompany = $event;
      var url = BASE_URL;
      if (courierCompany == "TCS") {
        url += "orders/bookTCS?orderId=" + id;
      } else if (courierCompany == "Leopard") {
        url += "orders/bookLeopard?orderId=" + id;
      } else {
        url +=
          "orders/bookRider?orderId=" +
          id +
          "&rider=" +
          courierCompany.toUpperCase();
      }

      axios
        .post(url, "", this.options)
        .then((response) => {
          console.log(response.data);

          if (response.data.hasError == true) {
            this.snackbar = true;
            this.snackBarText = response.data.message;
          } else if (response.status == 200) {
            this.snackbar = true;
            this.snackBarText = "Delivery company changed";
            this.addDeliverycompany(id, response.data);
          }
        })
        .catch((error) => {
          console.log(error);
          this.errored = true;
        });
    },
    addDeliverycompany(id, delivery) {
      for (var i = 0; i < this.orders.length; i++) {
        if (this.orders[i].id == id) {
          this.orders[i].order_delivery = delivery;
          break;
        }
      }
    },
    getCourierServiceColor(company) {
      if (company == "TCS") {
        return "#C72681";
      } else if (company == "LEOPARD") {
        return "#96be25";
      } else {
        return "#ff75c8";
      }
    },
    openCNDialog(id) {
      this.cnDialog = true;
      this.orderId = id;
    },
    updateCN() {
      var url =
        BASE_URL + "orders/cn?orderId=" + this.orderId + "&cn=" + this.cn;
      axios
        .put(url, "", this.options)
        .then((response) => {
          console.log(response.data);

          if (response.data.hasError == true) {
            this.snackbar = true;
            this.snackBarText = response.data.message;
          } else if (response.data.hasError == false) {
            this.snackbar = true;
            this.snackBarText = response.data.message;
            this.changeOrderCN();
            this.orderId = 0;
            this.cn = "";
            this.cnDialog = false;
          }
        })
        .catch((error) => {
          console.log(error);
          this.errored = true;
        });
    },
    changeOrderCN() {
      for (var i = 0; i < this.orders.length; i++) {
        if (this.orders[i].id == this.orderId) {
          this.orders[i].order_delivery.cn = this.cn;
          break;
        }
      }
    },

    getTrackingInformation(id) {
      this.syncing = true;
      var url = BASE_URL + "orders/track?orderId=" + id;
      axios
        .get(url, this.options)
        .then((response) => {
          console.log(response.data);

          if (response.data.hasError == true) {
            this.snackbar = true;
            this.snackBarText = response.data.message;
          } else if (response.status == 200) {
            this.syncing = false;
            this.order_tracking = response.data;
            this.trackingDialog = true;
          }
        })
        .catch((error) => {
          console.log(error);
          this.errored = true;
        });
    },
    sendReviewAgain(id) {
      var url = BASE_URL + "reviews/sendFeedback?id=" + id;
      axios
        .get(url, this.options)
        .then((response) => {
          console.log(response.data);

          if (response.data.hasError == true) {
            this.snackbar = true;
            this.snackBarText = response.data.message;
          } else if (response.data.hasError == false) {
            this.syncing = false;
            this.snackbar = true;
            this.snackBarText = response.data.message;
          }
        })
        .catch((error) => {
          console.log(error);
          this.errored = true;
        });
    },
    handleTrackingFileUpload(event) {
      const file = event.target.files[0];
      console.log(file);
      if (file) {
        let fileReader = new FileReader();
        fileReader.readAsArrayBuffer(file);
        fileReader.onload = (e) => {
          this.arrayBuffer = fileReader.result;
          var data = new Uint8Array(this.arrayBuffer);
          var arr = new Array();
          for (var i = 0; i != data.length; ++i)
            arr[i] = String.fromCharCode(data[i]);
          var bstr = arr.join("");
          var workbook = XLSX.read(bstr, { type: "binary" });
          var first_sheet_name = workbook.SheetNames[0];
          var worksheet = workbook.Sheets[first_sheet_name];
          var trackings = XLSX.utils.sheet_to_json(worksheet, { raw: true });
          console.log(trackings);
          var finalTrackings = [];
          for (var i = 0; i < trackings.length; i++) {
            if (trackings[i].OrderId != null) {
              var order = this.orders.find((el) => {
                return el.shopify_order_number == trackings[i].OrderId;
              });
              if (order != null) {
                var tracking = {
                  shopify_id: order.shopify_id,
                  cn: trackings[i].CN,
                  company: trackings[i].Company,
                };
                finalTrackings.push(tracking);
              }
            }
          }
          console.log(finalTrackings);
          this.bulkAddTracking(finalTrackings)
        };
      }
    },
    bulkAddTracking(finalTrackings) {
      var url = BASE_URL + `orders/bulkAddTracking`;
      axios
        .post(url, finalTrackings, this.options)
        .then((response) => {
          if (response.data.hasError == true) {
            this.snackbar = true;
            this.snackBarText = response.data.message;
          } else if (response.data.hasError == false) {
            this.syncing = false;
            this.snackbar = true;
            this.snackBarText = response.data.message;
          }
        })
        .catch((error) => {
          console.log(error);
          this.errored = true;
        })
        .finally(() => (this.loading = false));
    },
  },
};
</script>
  
  <style scoped>
.name {
  margin-top: 3%;
  margin-left: 1%;
}

.costPrice {
  margin: 0;
  padding-top: 0;
}

.syncButton {
  margin-top: 2%;
  float: right;
}

.search {
  margin: 2%;
}
.buttonStyle {
  margin-top: -2%;
  float: right;
}
.datatable {
  margin-top: 3%;
}
.shopify_id {
  font-size: 70%;
}
.status {
  margin: 5%;
  color: white;
  font-weight: bold;
  text-align: center;
  margin-top: 7%;
}

.date {
  font-size: 80%;
  font-weight: bold;
}
.v-select__selections {
  margin: 0;
  max-height: 5px !important;
  min-height: 5px !important;
  display: flex !important;
  align-items: center !important;
}

.cnEditIcon {
  margin-top: -25%;
}

.orderInfo {
  font-size: 80%;
  margin: 2%;
  margin-bottom: 10%;
  padding: 2%;
  text-align: center !important;
  align-items: center !important;
  border-radius: 20%;
}
</style>